import {Component, ElementRef, ViewChild} from '@angular/core';

import {VehiclesService} from '../../../../data/vehicles/vehicles.service';
import {VehicleImport} from '../../../../shared/models/vehicle-import';
import {JsonApiResponse} from '../../../../shared/models/JsonApiResponse';
import {MatStep, MatStepper, MatStepperNext} from '@angular/material/stepper';
import {AddOrImportVehicleDialogForm, AddOrImportVehicleResult} from './model/vehicle.types';
import {MatCard, MatCardActions, MatCardContent, MatCardHeader} from '@angular/material/card';
import {MatButton} from '@angular/material/button';
import {MatProgressBar} from '@angular/material/progress-bar';
import {NgPlural, NgTemplateOutlet} from '@angular/common';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable
} from '@angular/material/table';
import {MatIcon} from '@angular/material/icon';

@Component({
    selector: 'app-import-vehicles',
    templateUrl: './import-vehicles.component.html',
    styleUrls: ['./import-vehicles.component.scss'],
    imports: [
        MatStepper,
        MatStep,
        MatCard,
        MatCardHeader,
        MatCardContent,
        MatButton,
        MatCardActions,
        MatProgressBar,
        NgTemplateOutlet,
        MatStepperNext,
        NgPlural,
        MatTable,
        MatColumnDef,
        MatHeaderCell,
        MatHeaderCellDef,
        MatCellDef,
        MatCell,
        MatHeaderRow,
        MatHeaderRowDef,
        MatRowDef,
        MatRow,
        MatIcon
    ]
})
export class ImportVehiclesComponent implements AddOrImportVehicleDialogForm {

    @ViewChild('stepper') stepper: MatStepper;

    uiError: string;

    selectedFile: File;
    validData: VehicleImport[];
    invalidData: VehicleImport[];
    displayedColumnsValid: string[];
    displayedColumnsInvalid: string[];
    allColumns: {
        title: string;
        valid: boolean;
        invalid: boolean;
    }[] = [
        {title: 'line', valid: true, invalid: true},
        {title: 'action', valid: true, invalid: false},
        {title: 'errors', valid: false, invalid: true},
        {title: 'name', valid: true, invalid: true},
        {title: 'mapLabel', valid: true, invalid: true},
        {title: 'licensePlate', valid: true, invalid: true},
        {title: 'vGroup', valid: true, invalid: true},
    ];
    ImportVehiclesStateEnum = ImportVehiclesStateEnum;
    currentState = ImportVehiclesStateEnum.WAITING_FOR_FILE;
    showCsvHelp = false;

    @ViewChild('fileInput') fileInput: ElementRef;

    constructor(private readonly vehicleService: VehiclesService) {
        this.updateColumns();
    }

    handleFileInput(event: Event) {
        this.selectedFile = (event.target['files'] as FileList)[0];
        if (this.selectedFile) {
            this.upload();
        }
    }

    private upload() {
        this.currentState = ImportVehiclesStateEnum.UPLOADING_FILE;
        const formData: FormData = new FormData();
        formData.append('file', this.selectedFile, this.selectedFile.name);
        this.vehicleService.validateVehicleImport(formData).then(response => {
            this.validData = response.data.filter(row => row.errors == null || row.errors.length === 0);
            this.invalidData = response.data.filter(row => row.errors != null && row.errors.length > 0);
            this.currentState = ImportVehiclesStateEnum.UPLOAD_SUCCEEDED;
        }).catch(error => {
            this.uiError = error;
            this.currentState = ImportVehiclesStateEnum.UPLOAD_FAILED;
        });
    }

    hasValidRowsToImport(): boolean {
        const rowsToImport = this.rowsToImport();
        return !!rowsToImport && rowsToImport.length > 0;
    }

    rowsToImport(): VehicleImport[] {
        return this.validData;
    }

    canBeSaved(): boolean {
        return this.stepper.selectedIndex === 2
            && this.hasValidRowsToImport();
    }

    async save(): Promise<AddOrImportVehicleResult> {
        let result = await this.doImport();
        return {
            type: 'vehicleImport',
            data: result.data,
        } as AddOrImportVehicleResult;
    }

    private doImport(): Promise<JsonApiResponse<any>> {
        this.currentState = ImportVehiclesStateEnum.IMPORTING;

        const apiCallPromise = this.vehicleService.importVehicles(this.validData);
        apiCallPromise
            .then((_) => {
                this.currentState = ImportVehiclesStateEnum.IMPORT_SUCCEEDED;
            })
            .catch(error => {
                this.uiError = error;
                this.currentState = ImportVehiclesStateEnum.IMPORT_FAILED;
            });

        return apiCallPromise;
    }

    private updateColumns() {
        this.displayedColumnsValid = this.allColumns
            .filter(col => col.valid === true)
            .map(col => col.title);

        this.displayedColumnsInvalid = this.allColumns
            .filter(col => col.invalid === true)
            .map(col => col.title);
    }

}

enum ImportVehiclesStateEnum {
    WAITING_FOR_FILE,
    UPLOADING_FILE,
    UPLOAD_FAILED,
    UPLOAD_SUCCEEDED,
    IMPORTING,
    IMPORT_FAILED,
    IMPORT_SUCCEEDED
}
